import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { useParams, useLocation } from 'react-router-dom';

import { useNotification } from '../hooks/useNotification';
import { useSyncLanguageUrl } from '../hooks/useSyncLanguageUrl';
import BookNavigationTabs from '../components/BookNavigationTabs';

function ReleaseBook() {
  const { bookId } = useParams();
  const location = useLocation();
  const [pageLanguage, setPageLanguage] = useState(null);
  const { notify, Notification } = useNotification();
  const [isPreparing, setIsPreparing] = useState(false);
  const [progressMessage, setProgressMessage] = useState('');
  const [pdfUrl, setPdfUrl] = useState('');
  const pollInterval = useRef(null);
  const pollStartTime = useRef(null);
  const [overlayMessage, setOverlayMessage] = useState('');
  const [overlayType, setOverlayType] = useState('info');
  const [book, setBook] = useState(null);

  useSyncLanguageUrl(pageLanguage);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const langParam = searchParams.get('plang');
    if (langParam) {
      setPageLanguage(langParam);
    }
  }, [location.search]);

  useEffect(() => {
    const fetchBook = async () => {
      try {
        const res = await axios.get(`/api/book/${bookId}`);
        setBook(res.data);
      } catch (err) {
        console.error('Failed to fetch book info:', err);
        setBook(null);
      }
    };

    if (bookId && pageLanguage) {
      fetchBook();
    }
  }, [bookId, pageLanguage]);

  const startPollingPDFStatus = () => {
    pollStartTime.current = Date.now();

    pollInterval.current = setInterval(async () => {
      const elapsed = Date.now() - pollStartTime.current;
      if (elapsed > 3 * 60 * 1000) {
        clearInterval(pollInterval.current);
        setProgressMessage('PDF generation timed out.');
        setOverlayMessage('');

        return;
      }

      try {
        const response = await axios.get('/api/check-pdf-status');
        if (response.data.ready && response.data.pdf_url) {
          clearInterval(pollInterval.current);
          setPdfUrl(response.data.pdf_url);
          setOverlayType('success');
          setOverlayMessage('🎉 Your book PDF is ready!'); // Clear old spinner
        } else if (response.data.error) {
          clearInterval(pollInterval.current);

          setOverlayType('error');
          setOverlayMessage(`❌ ${response.data.error}`);
          setProgressMessage('');
        }
      } catch (err) {
        console.error('Error polling PDF status:', err);
        setProgressMessage('Error while checking PDF status.');
        clearInterval(pollInterval.current);
      }
    }, 10000); // every 10 seconds
  };

  const handleExportBook = async () => {
    setIsPreparing(true);
    setProgressMessage('Preparing book...');

    try {
      const response = await axios.post('/api/prepare-book-missing-previews', {
        book_id: bookId,
        language: pageLanguage,
      });

      if (response.status === 200) {
        handleExternalExport();
      } else {
        setProgressMessage(response.data.error || 'Unknown error.');
      }
    } catch (err) {
      console.error(err);
      setProgressMessage('Server error: ' + err.message);
    } finally {
      setIsPreparing(false);
    }
  };

  const handleExternalExport = async () => {
    try {
      setProgressMessage('');
      setOverlayType('info');
      setOverlayMessage('Generating your book PDF...');
      setPdfUrl('');

      const response = await axios.post('/api/export-external-book-pdf', {
        book_id: bookId,
        language: pageLanguage,
      });

      if (response.status === 202) {
        startPollingPDFStatus(); // ⬅️ Start polling now
      } else {
        notify(response.data?.error || 'Failed to start export', 'error');
      }
    } catch (err) {
      console.error(err);
      notify('Error sending request to Lambda');
    }
  };

  return (
    <>
      <Notification />
      <div className="max-w-[1400px] mx-auto p-6 bg-white shadow-lg rounded-lg mt-10">
        <BookNavigationTabs
          bookId={bookId}
          currentLanguage={pageLanguage}
          headerTitle={book?.name || 'Release'}
          headerLinkTo={`/books/${bookId}/edit`}
          showBackButton
          showBookSwitcher
          languageSelector={
            <>
              <span className="text-sm text-gray-600">Current Profile:</span>
              <select
                value={pageLanguage}
                onChange={(e) => setPageLanguage(e.target.value)}
                className="border border-gray-300 rounded py-1 px-2 text-sm"
              >
                <option value="es_de">ES-DE</option>
                <option value="es_en">ES-EN</option>
                <option value="es_ru">ES-RU</option>
                <option value="es_it">ES-IT</option>
                <option value="es_tr">ES-TR</option>
                <option value="es_fr">ES-FR</option>
                <option value="es_pl">ES-PL</option>
                <option value="de_nl">DE-NL</option>
                <option value="de_ru">DE-RU</option>
                <option value="de_en">DE-EN</option>
                <option value="de_it">DE-IT</option>
                <option value="de_fr">DE-FR</option>
                <option value="de_tr">DE-TR</option>
                <option value="de_pl">DE-PL</option>
                <option value="en_nl">EN-NL</option>
                <option value="en_pl">EN-PL</option>
                <option value="en_it">EN-IT</option>
                <option value="en_fr">EN-FR</option>
                <option value="en_tr">EN-TR</option>
                <option value="en_ru">EN-RU</option>
              </select>
            </>
          }
        />
        <div className="justify-center items-center flex gap-2 mb-2">
          <button
            onClick={handleExportBook}
            disabled={isPreparing}
            className="bg-green-600 text-white px-10 py-1 rounded disabled:opacity-50"
          >
            Generate PDF
          </button>
        </div>
        <div className="flex flex-row flex-grow mt-1">
          <main className="flex-grow p-4">
            <div className="flex flex-col items-center gap-4 max-w-md mx-auto">
              {/* --- Progress Message --- */}
              {progressMessage && (
                <p className="text-center text-gray-700">{progressMessage}</p>
              )}

              {overlayMessage && (
                <div
                  className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center"
                  onClick={() => setOverlayMessage('')}
                >
                  <div
                    className="bg-white p-6 rounded-xl shadow-xl text-center max-w-sm relative"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {/* X Button */}
                    <button
                      className="absolute top-2 right-2 text-gray-400 hover:text-black"
                      onClick={() => setOverlayMessage('')}
                    >
                      ✕
                    </button>

                    {/* Main message */}
                    <p
                      className={`text-lg font-semibold mb-4 ${
                        overlayType === 'error'
                          ? 'text-red-600'
                          : 'text-gray-800'
                      }`}
                    >
                      {overlayMessage}
                    </p>

                    {/* Spinner for info type */}
                    {overlayType === 'info' && (
                      <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto" />
                    )}

                    {/* PDF download link for success */}
                    {overlayType === 'success' && pdfUrl && (
                      <a
                        href={pdfUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-block bg-blue-600 text-white px-4 py-2 rounded shadow hover:bg-blue-700"
                      >
                        Download PDF
                      </a>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="justify-center items-center flex gap-2 mb-0">
              <h3 className="text-sm font-semibold mt-10">
                Latest book created:
              </h3>
            </div>
            <div className="justify-center items-center flex gap-2 mt-2">
              {book && book[`pdf_url_${pageLanguage}`] ? (
                <a
                  href={book[`pdf_url_${pageLanguage}`]}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block text-black text-center py-2 underline"
                >
                  {book[`name`]}
                </a>
              ) : (
                <p className="text-sm text-gray-500">No book available yet.</p>
              )}
            </div>
          </main>
        </div>
      </div>
    </>
  );
}

export default ReleaseBook;
